import { Search } from '@mui/icons-material';
import {
    Autocomplete,
    FormControlLabel,
    InputAdornment,
    type InputProps,
    Radio,
    Skeleton,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import type { Dispatch, SetStateAction } from 'react';
import { _ } from '/@/locale';

interface SelectItem {
    id: number;
    label: string;
    disabled?: string;
}

const SelectCheckbox = ({
    InputProps,
    items,
    selectedItem,
    setSelectedItem,
    verbose: _verbose,
    loading = false,
}: {
    InputProps?: InputProps;
    items: SelectItem[];
    selectedItem: number | null;
    setSelectedItem: Dispatch<SetStateAction<number | null>>;
    verbose?: string;
    loading?: boolean;
}) => {
    const selectedOption = items.find((item) => item.id === selectedItem);

    return (
        <Stack spacing={3} sx={{ width: '100%', pb: '300px' }}>
            {loading ? (
                <Stack spacing={1}>
                    <Skeleton variant='rounded' height={56} />
                    <Skeleton variant='text' height={40} />
                    <Skeleton variant='text' height={40} />
                    <Skeleton variant='text' height={40} />
                </Stack>
            ) : (
                <Autocomplete
                    options={items}
                    value={selectedOption}
                    onChange={(_event, newValue) => {
                        setSelectedItem(newValue?.id ?? null);
                    }}
                    getOptionLabel={(option) => option.label}
                    getOptionDisabled={(option) => !!option.disabled}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    filterOptions={(options, { inputValue }) => {
                        const searchTerm = inputValue.toLowerCase().trim();
                        if (!searchTerm) return options;
                        return options.filter((option) => option.label.toLowerCase().includes(searchTerm));
                    }}
                    open
                    disableCloseOnSelect
                    disableClearable
                    noOptionsText={_('No se encontraron resultados. Revisa el nombre o apellido ingresado.')}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder={InputProps?.placeholder || _('Buscar...')}
                            color='secondary'
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <InputAdornment position='start'>
                                        <Search />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    )}
                    renderOption={(props, option) => {
                        const isSelected = selectedItem === option.id;
                        const isDisabled = !!option.disabled;
                        return (
                            <li {...props} key={option.id}>
                                <FormControlLabel
                                    control={
                                        <Radio
                                            checked={isSelected}
                                            color='secondary'
                                            sx={{ p: 0.5 }}
                                            disabled={isDisabled}
                                        />
                                    }
                                    label={
                                        <Typography variant='body1' color={'primary'} sx={{ ml: 1 }}>
                                            {option.label}
                                        </Typography>
                                    }
                                    sx={{ m: 0, width: '100%' }}
                                />
                            </li>
                        );
                    }}
                    ListboxProps={{
                        sx: {
                            maxHeight: 300,
                            '& .MuiAutocomplete-option': {
                                py: 1,
                                borderBottom: (t) => `1px solid ${t.palette.divider}`,
                                '&:last-child': {
                                    borderBottom: 'none',
                                },
                            },
                        },
                    }}
                    sx={{ width: '100%' }}
                />
            )}
        </Stack>
    );
};

export default SelectCheckbox;
